import { DateTime } from "luxon";
import { useHospitalApp } from "./useHospitalApp";

/**
 * Date functions that act on the current hospitals time zone
 */
export type useHospitalTimeReturn = {
    /**
     * Convert a date to the current hospitals time zone
     * Example Input: DateTime in UTC
     * @returns DateTime in hospitals time zone with time changed to hospital time
     */
    getHospitalDate: (date: DateTime) => DateTime;
    /**
     * Convert a date to the current hospitals time zone and convert it to a short date string
     * Example Input: DateTime on 9/27/2022 at 12:30 AM UTC
     * @returns  9/26/2022 (hospital time zone still on previous day)
     */
    getHospitalShortDate: (date: DateTime) => string;
    /**
     * Convert a date to the current hospitals time zone and convert it to a simple time string
     * Example Input: DateTime on 9/26/2022 at 2:32 PM UTC
     * @returns 9:26, time shift to hospitals time zone is 5 hours before utc here
     */
    getHospitalSimpleTime: (date: DateTime) => string;
}

function internalGetDate(date: DateTime, timeZone: string) {
    return date.setZone(timeZone);
}

function internalSimpleTime(date: DateTime, timeZone: string) {
    const lDate = internalGetDate(date, timeZone);
    return lDate.toLocaleString(DateTime.TIME_SIMPLE);
}

function internalShortDate(date: DateTime, timeZone: string) {
    const lDate = internalGetDate(date, timeZone);
    return lDate.toLocaleString(DateTime.DATE_SHORT);
}

/**
 * A hook that uses the hospitalApp hook to get the current hospitals timeZone
 * and returns helper date functions to get Date's, display dates, and display time
 * that has been converted to the hospitals timezone.
 * @returns a useHospitalTimeReturn object that contains all the date functions currently encapsulated.
 */
export const useHospitalTime = (): useHospitalTimeReturn => {
    const hospitalApp = useHospitalApp();
    const timeZone = hospitalApp.currentHospital?.store?.timezoneName ?? 'UTC';
    return {
        getHospitalDate: (date: DateTime) => internalGetDate(date, timeZone),
        getHospitalShortDate: (date: DateTime) => internalShortDate(date, timeZone),
        getHospitalSimpleTime: (date: DateTime) => internalSimpleTime(date, timeZone)
    }
}
